import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { mutateOrganizationCreate } from '@/api/organization';
import SignInPanel from '@/app/sign-in/SignInPanel';
import { useAppForm } from '@/components/Form';
import { FieldDescription, FieldLabel } from '@/components/Form/fields';
import { FullPage } from '@/components/page';
import { Link, Text } from '@/components/typography';
import { maxDomainLength, sessionDurationMinutes } from '@/constants/numbers';
import { INSTANCE_ERROR_CODES } from '@/error/messages/instance-standard-errors';
import getSolStandardError from '@/error/sol-standard-errors';
import { isDomainValid } from '@/helper/validator';
import useLocationState from '@/hooks/useLocationState';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

const SAVE_TAKING_LONG_TIME_DELAY = 5000;

const ConfigureOrg = () => {
  const { intermediateToken } = useLocationState<{ intermediateToken: string }>();
  const navigate = useNavigate();
  const stytch = useStytchB2BClient();
  const { pushNotification } = useNotification();
  const [saveIsTakingLongTime, setSaveIsTakingLongTime] = useState(false);
  const stillSavingTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const formSchema = z.object({
    domain: z
      .string()
      .max(maxDomainLength, t`Domain must be less than ${maxDomainLength} characters`)
      .refine(
        (value) => isDomainValid(value),
        t`Values should be valid domain names (e.g. example.com), with no subdomains (e.g. invalid.com/subdomain), and must not start with "http" or "www".`,
      ),
  });

  const form = useAppForm({
    defaultValues: {
      domain: '',
    },
    validators: {
      onSubmit: formSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          stillSavingTimeout.current = setTimeout(
            () => setSaveIsTakingLongTime(true),
            SAVE_TAKING_LONG_TIME_DELAY,
          );

          const response = await mutateOrganizationCreate(value.domain, intermediateToken);
          const { data } = response ?? {};

          if (!data.success) {
            throw new Error();
          }

          stytch.session.updateSession({
            session_token: data.sessionToken,
            session_jwt: data.sessionJwt,
          });

          await stytch.session.authenticate({
            session_duration_minutes: sessionDurationMinutes,
          });
          await stytch.self.get();

          // We should be authenticated now, head to the app.
          navigate(ROUTES.app.path, { replace: true });
          // force a refresh
          navigate(0);
        } catch (e) {
          const solError = getSolStandardError(e);

          if (solError.extensions.code === INSTANCE_ERROR_CODES.INTERMEDIATE_SESSION_EXPIRED) {
            pushNotification({
              type: 'error',
              message: (
                <Trans>
                  {solError.message}&nbsp;
                  <Link
                    variant="caption1"
                    color="static-white"
                    to={ROUTES.signUp.path}
                  >{t`Try to sign up again?`}</Link>
                </Trans>
              ),
            });
          } else {
            pushNotification({
              type: 'error',
              message: solError.message ?? t`An error occurred while creating your account`,
            });
          }
        } finally {
          setSaveIsTakingLongTime(false);

          if (stillSavingTimeout.current) {
            clearTimeout(stillSavingTimeout.current);
            stillSavingTimeout.current = undefined;
          }
        }
      },
    },
  });

  return (
    <FullPage title={t`Configure Organization`}>
      <SignInPanel
        title={<Trans>Enter your Company's Domain</Trans>}
        description={
          <Trans>
            Your domain will be used to create a new Account. Please use your top-level domain
            without subdomains.
          </Trans>
        }
      >
        <form
          className="flex w-full max-w-md flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            form.handleSubmit();
          }}
        >
          <form.AppField name="domain">
            {(field) => (
              <field.Field>
                <FieldLabel htmlFor="domain">
                  <Trans>Company Domain</Trans>
                </FieldLabel>
                <field.TextInput id="domain" placeholder={t`example.com`} size="large" />
                <FieldDescription>
                  <Trans>
                    Only use your top level domain without subdomains (example channel99.com or
                    yourdomain.io).
                  </Trans>
                </FieldDescription>
                <field.Error />
              </field.Field>
            )}
          </form.AppField>
          <form.AppForm>
            <form.SubmitButton variant="primary" size="large" block>
              <Trans>Create account</Trans>
            </form.SubmitButton>
          </form.AppForm>
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) =>
              isSubmitting &&
              saveIsTakingLongTime && (
                <Text className="mt-6 text-center text-aqua-400" variant="body2" weight="bold">
                  <Trans>Please wait while we create your new account</Trans>
                </Text>
              )
            }
          </form.Subscribe>
        </form>
      </SignInPanel>
    </FullPage>
  );
};

export default ConfigureOrg;
